import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useResolvedPath, Location, NavigateFunction } from 'react-router-dom';
import { KeyManagementTab } from './KeyManagementRoutes.types';
import { getAppointment, IAppointment, insertAuditTrail } from '../../../API/GoMoto';
import { Events } from '../../../API/Enums';
import { useAdminState } from '../../../context/Admin/admin';
import { useLocker } from '../../../context/hardware';
import { SimpleKeyboard } from '../../../controls/SimpleKeyboard/SimpleKeyboard';
import { Tab, Tabs } from '../../../controls/Tabs/Tabs';
import { ScrollableBody } from '../../Layout/ScrollableBody';
import { StoreKeyRoutes } from './StoreKeyRoutes/StoreKeyRoutes';
import { KeyMap } from './KeyMap/KeyMap';
import { KeyManagementHeader } from '../../../controls/KeyManagementHeader/KeyManagementHeader';
import { CheckIns } from './CheckIns/CheckIns';
import { Admin_KeyManagementRoutePaths, PathName } from '../../../helpers/Routes';
import { LockerOpen } from './LockerOpen/LockerOpen';
import { useLocalization } from '../../../context/Localization/localization';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentStateUnsafe } from '../../../context/Appointment/appointment';
import { GoMotoComponent, GoMotoFunction } from '../../../API/GoMoto.types';
import { LocalizationType } from '../../../context/Localization/localization.types';
import { Display } from './Display/Display';
import { AdminLockerOperationDispatch, AdminLockerOperationDispatchTypes } from '../../../context/AdminLockerOperation/adminLockerOperation.types';
import { useAdminLockerOperationDispatch, useAdminLockerOperationState } from '../../../context/AdminLockerOperation/adminLockerOperation';
import { useDevelopmentState } from '../../../context/Development/Development';
import { AdminFiltersProvider } from '../../../context/AdminFilters/AdminFilters';

/**
 * This component provides the state management and the routing for the Key Management screen
 * and also contains the routing logic for the StoreKey routing component.
 */
export const KeyManagementRoutes: GoMotoComponent = () => {
    const Localization: LocalizationType = useLocalization();
    const { isDev, hideKeyboard: devHideKeyboard } = useDevelopmentState();
    const { lockersEnabled } = useLocker();
    const { userID, canAccessDealer } = useAdminState();
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();
    const appointmentDispatch = useAppointmentDispatch();
    const { returnPath, repullAppointment, isAdminCreate } = useAdminLockerOperationState();
    const adminLockerOperationDispatch: AdminLockerOperationDispatch = useAdminLockerOperationDispatch();
    const { pathname } = useResolvedPath('.');
    const navigate: NavigateFunction = useNavigate();
    const location: Location<unknown> = useLocation();
    const [keyUpdated, setKeyUpdated] = useState<boolean>(false);

    let visibleTabs: KeyManagementTab[] = [];

    if (lockersEnabled && canAccessDealer) {
        visibleTabs = ['LOADED_KEYS', 'CHECK_INS', 'CREATE', 'KEY_MAP'];
    }
    else if (lockersEnabled && !canAccessDealer) {
        visibleTabs = ['LOADED_KEYS', 'CHECK_INS', 'KEY_MAP'];
    }
    else if (!lockersEnabled && canAccessDealer) {
        visibleTabs = ['CHECK_INS', 'CREATE'];
    }
    else /* !lockersEnabled && !canAccessDealer */ {
        visibleTabs = ['CHECK_INS'];
    }

    const getTabIndex: GoMotoFunction<[KeyManagementTab], number> = (tab) => {
        return visibleTabs.indexOf(tab);
    };

    const tabIndex: MutableRefObject<number> = useRef<number>(0);

    useEffect(() => {
        tabIndex.current = 0;
    }, [location.pathname, lockersEnabled]);

    return (
        <Routes>
            <Route
                path={PathName(Admin_KeyManagementRoutePaths.AppointmentDetails, '*')}
                element={appointment ? (
                    <Display
                        onProceed={() => {
                            navigate(Admin_KeyManagementRoutePaths.LockerOpen, { replace: true });
                        }}
                        onClose={() => {
                            navigate(pathname);
                        }}
                        onStoreKey={() => {
                            if (isAdminCreate) {
                                adminLockerOperationDispatch({ type: AdminLockerOperationDispatchTypes.SetIsAdminCreate,
                                    payload: false });
                            }

                            adminLockerOperationDispatch({
                                type: AdminLockerOperationDispatchTypes.SetRepullAppointment,
                                payload: true,
                            });

                            navigate(PathName(pathname, Admin_KeyManagementRoutePaths.StoreKey));
                        }}
                        onReturn={() => {
                            appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
                            navigate(pathname);
                        }}
                    />
                ) : null}
            />
            <Route
                path={PathName(Admin_KeyManagementRoutePaths.StoreKey, '*')}
                element={appointment || isAdminCreate ? (
                    <StoreKeyRoutes
                        onProceed={() => {
                            adminLockerOperationDispatch({ type: AdminLockerOperationDispatchTypes.Reset });

                            navigate(pathname);
                        }}
                        repullAppt={repullAppointment}
                        isAdminCreate={isAdminCreate}
                    />
                ) : null}
            />
            <Route
                path={PathName(Admin_KeyManagementRoutePaths.LockerOpen, '*')}
                element={
                    <LockerOpen
                        onProceed={async () => {
                            if (appointment) {
                                const appt = await getAppointment(appointment.key);
                                if (appt) {
                                    appointmentDispatch({
                                        type: AppointmentDispatchTypes.Update,
                                        payload: appt,
                                    });
                                }
                            }

                            if (returnPath) {
                                navigate(returnPath, { replace: true });
                            }
                            else {
                                tabIndex.current = getTabIndex('KEY_MAP');
                                navigate(pathname, { replace: true });
                            }

                            adminLockerOperationDispatch({ type: AdminLockerOperationDispatchTypes.Reset });
                        }}
                    />
                }
            />
            <Route
                path={'*'}
                // [ ]: This should probably be it's own component to maintain this component simply being a routing component.
                element={
                    <>
                        <ScrollableBody heading={<KeyManagementHeader />}>
                            <Tabs initialTabIndex={tabIndex.current}>
                                {lockersEnabled && (
                                    <Tab
                                        tabIndex={getTabIndex('LOADED_KEYS')}
                                        label={Localization.LoadedKeys}
                                        hideKeyboard
                                        lazy
                                    >
                                        <AdminFiltersProvider tabType={'loaded-keys'}>
                                            <CheckIns
                                                onAppointmentSelected={(appt) => {
                                                    if (appt) {
                                                        appt.key && insertAuditTrail(appt.key, Events.DetailsViewed, userID);
                                                        appointmentDispatch({
                                                            type: AppointmentDispatchTypes.Update,
                                                            payload: appt,
                                                        });
                                                        navigate(PathName(pathname, Admin_KeyManagementRoutePaths.AppointmentDetails));
                                                    }
                                                }}
                                                keyUpdated={keyUpdated}
                                                setKeyUpdated={setKeyUpdated}
                                                tabType={'loaded-keys'}
                                            />
                                        </AdminFiltersProvider >
                                    </Tab>
                                )}
                                <Tab
                                    tabIndex={getTabIndex('CHECK_INS')}
                                    label={Localization.CheckIns}
                                    hideKeyboard
                                    lazy
                                >
                                    <AdminFiltersProvider tabType={'check-ins'}>
                                        <CheckIns
                                            onAppointmentSelected={(appt) => {
                                                if (appt) {
                                                    insertAuditTrail(appt.key, Events.DetailsViewed, userID);
                                                    appointmentDispatch({
                                                        type: AppointmentDispatchTypes.Update,
                                                        payload: appt,
                                                    });
                                                    navigate(PathName(pathname, Admin_KeyManagementRoutePaths.AppointmentDetails));
                                                }
                                            }}
                                            onAddKeyForAppointment={(appt) => {
                                                if (appt) {
                                                    appointmentDispatch({
                                                        type: AppointmentDispatchTypes.Update,
                                                        payload: appt,
                                                    });
                                                    navigate(PathName(pathname, Admin_KeyManagementRoutePaths.StoreKey));
                                                }
                                            }}
                                            keyUpdated={keyUpdated}
                                            setKeyUpdated={setKeyUpdated}
                                            tabType={'check-ins'}
                                        />
                                    </AdminFiltersProvider>
                                </Tab >
                                {canAccessDealer && (
                                    <Tab
                                        tabIndex={getTabIndex('CREATE')}
                                        label={Localization.Create}
                                        onClick={() => {
                                            if (appointment !== undefined) {
                                                appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
                                            }

                                            adminLockerOperationDispatch({
                                                type: AdminLockerOperationDispatchTypes.SetIsAdminCreate,
                                                payload: true,
                                            });
                                        }}
                                        lazy
                                    >
                                        <Navigate to={PathName(pathname, Admin_KeyManagementRoutePaths.StoreKey)} />
                                    </Tab>
                                )}
                                {
                                    lockersEnabled && (
                                        <Tab
                                            tabIndex={getTabIndex('KEY_MAP')}
                                            label={Localization.KeyMap}
                                            hideKeyboard
                                            lazy
                                        >
                                            <KeyMap
                                                onProceed={() => {
                                                    navigate(Admin_KeyManagementRoutePaths.LockerOpen);
                                                }}
                                                setKeyUpdated={setKeyUpdated}
                                            />
                                        </Tab>
                                    )
                                }
                            </Tabs >
                        </ScrollableBody >
                        {!isDev || (isDev && !devHideKeyboard) ? (
                            <SimpleKeyboard baseClass={'Checkins'} />
                        ) : null}
                    </>
                }
            />
        </Routes >
    );
};